import { parseURL, withLeadingSlash } from 'ufo'
import { useTimeAgoIntl } from '@vueuse/core'

export const useI18nTools = () => {
  /**
   * 传入ISO8601返回格式化本地时间字符串
   * @param time 时间字符串
   */
  const formatLocaleTime = (time: string): string => {
    const { locale } = useI18n()
    const formatCfg: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      // 格式化时区名称
      timeZoneName: 'short',
      // 显示公元
      // era: 'long',
    }
    const localStr = new Date(time).toLocaleString(locale.value, formatCfg)
    return localStr
  }

  /**
   * 将时间转换为相对时间
   * @param curLocale 当前语言环境
   * @param time 时间字符串
   */
  const formatTimeAgo = (time: string) => {
    const { locale } = useI18n()
    return useTimeAgoIntl(new Date(time), { locale: locale.value }).value
  }

  /**
   * 辅助函数，将path字符串移除语言前缀后，再存入数组，便于其他函数格式化
   * @param path - 输入路径
   * @param locale - 当前语言代码
   * @example /vi/aa/bb-cc -> ['aa','bb-cc']
   */
  function getCleanSegments(path: string, locale: string): string[] {
    //   useDPrint('getCleanSegments处理path', path)
    const { pathname } = parseURL(path)
    let segments = pathname.split('/').filter(Boolean)
    if (segments[0] === locale) {
      segments.shift()
    }
    if (segments.length === 0) {
      segments = ['index']
    }
    //   useDPrint('getCleanSegments返回path', segments)
    return segments
  }

  /**
   * 将path字符串移除语言前缀
   * @param path - 输入路径
   * @example /vi/aa/bb-cc -> /aa/bb-cc
   */
  function formatOrigPath(path?: string): ComputedRef<string> {
    const route = useRoute()
    const { locale } = useI18n()
    const res = computed(
      () => withLeadingSlash(getCleanSegments(path || route.path, locale.value).join('/')) || '/',
    )
    //   useDPrint('formatOrigPath调用', `${path}->${res.value}`)
    // 重新组合，保持斜杠规范
    return res
  }

  /**
   * 将path字符串移除语言前缀后，再将斜杠分隔符替换点
   * @param path - 输入路径
   * @example /zh-hans/aa/bb-cc -> aa.bb-cc
   */
  function formatPathDot(path?: string): ComputedRef<string> {
    const route = useRoute()
    const { locale } = useI18n()
    const res = computed(() => getCleanSegments(path || route.path, locale.value).join('.'))
    //   useDPrint('formatPathDot调用', `${path}->${res.value}`)
    return res
  }

  /**
   * 计算传入的时区偏移 与 当前本地时区的 时差（小时）
   * @param {number} targetOffset - 目标时区偏移（小时），如 8 表示 UTC+8
   * @returns {Object} { diff: number, description: string }
   *   diff > 0 表示目标时区比本地时间 **快** diff 小时
   *   diff < 0 表示目标时区比本地时间 **慢** |diff| 小时
   */
  function getTimezoneDifference(targetOffset: number) {
    const { t } = useI18n()
    // 获取本地时区相对于 UTC 的偏移（小时）
    // 注意：getTimezoneOffset() 返回分钟，且符号与常用 UTC 偏移相反
    const localOffsetMinutes = new Date().getTimezoneOffset() // 例如北京时间返回 -480
    const localOffsetHours = -localOffsetMinutes / 60 // 转换为常用形式：北京时间得到 +8
    // 计算目标时区与本地时区的差值（小时）
    const diff = targetOffset - localOffsetHours
    let description = ''
    if (diff > 0) {
      description = t('placeholder.timeDifference.later', { diff: diff })
    } else if (diff < 0) {
      description = t('placeholder.timeDifference.earlier', { diff: Math.abs(diff) })
    } else {
      description = t('placeholder.timeDifference.same')
    }
    return {
      diff: Number(diff.toFixed(2)), // 保留两位小数，应对夏令时等小数情况
      localOffset: localOffsetHours,
    }
  }

  return { formatLocaleTime, formatTimeAgo, formatOrigPath, formatPathDot, getTimezoneDifference }
}
